import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie,
} from 'recharts';
import { Building2, CalendarDays, Clock, TrendingUp, RefreshCw, Plus, Pencil, Trash2, X, Check, BedDouble, Wifi, Wind, Tv, Lock, Waves, Eye, Bath, ParkingCircle, Baby, Utensils, Zap, Accessibility, ChevronDown } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app/api';
const token = () => sessionStorage.getItem('qbdp_token');

// ── Unités — données de référence génériques ────────────────────────────────

const TYPES_UNITE = [
  { value: 'simple',       label: 'Simple / Standard' },
  { value: 'double',       label: 'Double' },
  { value: 'twin',         label: 'Twin (2 lits séparés)' },
  { value: 'triple',       label: 'Triple' },
  { value: 'suite',        label: 'Suite' },
  { value: 'suite_junior', label: 'Suite Junior' },
  { value: 'studio',       label: 'Studio' },
  { value: 'appartement',  label: 'Appartement' },
  { value: 'familiale',    label: 'Familiale' },
  { value: 'pmr',          label: 'PMR / Accessible' },
  { value: 'prestige',     label: 'Prestige / Vue' },
  { value: 'bungalow',     label: 'Bungalow / Chalet' },
  { value: 'mobil_home',   label: 'Mobile Home' },
  { value: 'emplacement',  label: 'Emplacement' },
  { value: 'box',          label: 'Box / Salle' },
  { value: 'autre',        label: 'Autre' },
];

const STATUTS_UNITE = [
  { value: 'disponible',   label: 'Disponible',    color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  { value: 'occupee',      label: 'Occupée',       color: '#1a56db', bg: '#eff6ff', border: '#bfdbfe' },
  { value: 'maintenance',  label: 'Maintenance',   color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { value: 'hors_service', label: 'Hors service',  color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
];

const CARACT_GROUPS = [
  { label: 'Confort', items: [
    { id: 'wifi',       label: 'Wi-Fi',              Icon: Wifi },
    { id: 'clim',       label: 'Climatisation',      Icon: Wind },
    { id: 'tv',         label: 'Télévision',         Icon: Tv },
    { id: 'coffre',     label: 'Coffre-fort',        Icon: Lock },
    { id: 'minibar',    label: 'Minibar',            Icon: Utensils },
    { id: 'chauffage',  label: 'Chauffage',          Icon: Zap },
  ]},
  { label: 'Salle de bain', items: [
    { id: 'sdb_privee', label: 'Salle de bain privée', Icon: Bath },
    { id: 'douche',     label: 'Douche',             Icon: Waves },
    { id: 'baignoire',  label: 'Baignoire',          Icon: Bath },
    { id: 'wc_separe',  label: 'WC séparé',          Icon: Building2 },
  ]},
  { label: 'Vue & Extérieur', items: [
    { id: 'vue_mer',    label: 'Vue mer',            Icon: Eye },
    { id: 'vue_jardin', label: 'Vue jardin',         Icon: Eye },
    { id: 'vue_ville',  label: 'Vue ville',          Icon: Eye },
    { id: 'balcon',     label: 'Balcon',             Icon: Building2 },
    { id: 'terrasse',   label: 'Terrasse',           Icon: Building2 },
  ]},
  { label: 'Services', items: [
    { id: 'parking',    label: 'Parking',            Icon: ParkingCircle },
    { id: 'animaux',    label: 'Animaux acceptés',   Icon: Baby },
    { id: 'lit_bebe',   label: 'Lit bébé dispo',     Icon: Baby },
    { id: 'petit_dej',  label: 'Petit-déj inclus',   Icon: Utensils },
  ]},
  { label: 'Équipements', items: [
    { id: 'cuisine',    label: 'Cuisine équipée',    Icon: Utensils },
    { id: 'lave_linge', label: 'Lave-linge',         Icon: Waves },
    { id: 'frigo',      label: 'Réfrigérateur',      Icon: Zap },
  ]},
  { label: 'Accessibilité', items: [
    { id: 'pmr',        label: 'Accès PMR',          Icon: Accessibility },
    { id: 'sdb_pmr',    label: 'Salle de bain PMR',  Icon: Accessibility },
  ]},
];

const STATUT_INFO = (v) => STATUTS_UNITE.find(s => s.value === v) || STATUTS_UNITE[0];

function StatutBadge({ statut }) {
  const s = STATUT_INFO(statut);
  return <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 10, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

const EMPTY_UNITE = { numero: '', nom: '', type_unite: 'simple', capacite: 2, surface_m2: '', etage: '', batiment: '', tarif_base: '', caracteristiques: [], description: '', statut: 'disponible', porte_id: '' };

function ModalUnite({ unite, onSave, onClose }) {
  const [form, setForm]   = useState(unite ? { ...unite, caracteristiques: unite.caracteristiques || [] } : { ...EMPTY_UNITE });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCaract = (id) => setForm(f => ({
    ...f,
    caracteristiques: f.caracteristiques.includes(id) ? f.caracteristiques.filter(c => c !== id) : [...f.caracteristiques, id],
  }));

  const inp = { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const sel = { ...inp, background: 'white' };

  const handleSave = async () => {
    if (!form.numero && !form.nom) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{unite ? 'Modifier l\'unité' : 'Ajouter une unité'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} color="#6b7280" /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Numéro *</div>
            <input style={inp} placeholder="101, A12, Suite 1…" value={form.numero} onChange={e => set('numero', e.target.value)} /></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Nom (optionnel)</div>
            <input style={inp} placeholder="Suite Présidentielle…" value={form.nom} onChange={e => set('nom', e.target.value)} /></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Type</div>
            <select style={sel} value={form.type_unite} onChange={e => set('type_unite', e.target.value)}>
              {TYPES_UNITE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Statut</div>
            <select style={sel} value={form.statut} onChange={e => set('statut', e.target.value)}>
              {STATUTS_UNITE.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Capacité (personnes)</div>
            <input style={inp} type="number" min={1} max={20} value={form.capacite} onChange={e => set('capacite', e.target.value)} /></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Surface (m²)</div>
            <input style={inp} type="number" min={1} placeholder="24" value={form.surface_m2} onChange={e => set('surface_m2', e.target.value)} /></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Étage</div>
            <input style={inp} type="number" placeholder="0 = RDC" value={form.etage} onChange={e => set('etage', e.target.value)} /></div>
          <div><div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Bâtiment / Aile</div>
            <input style={inp} placeholder="Bâtiment A, Aile Nord…" value={form.batiment} onChange={e => set('batiment', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Tarif de base (€ / nuit)</div>
            <input style={{ ...inp, maxWidth: 180 }} type="number" min={0} step={0.01} placeholder="120.00" value={form.tarif_base} onChange={e => set('tarif_base', e.target.value)} />
          </div>
        </div>

        {/* Caractéristiques */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Caractéristiques & équipements</div>
          {CARACT_GROUPS.map(g => (
            <div key={g.label} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>{g.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {g.items.map(({ id, label, Icon }) => {
                  const active = form.caracteristiques.includes(id);
                  return (
                    <button key={id} type="button" onClick={() => toggleCaract(id)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', border: `1px solid ${active ? '#1a56db' : '#e5e7eb'}`, background: active ? '#eff6ff' : 'white', color: active ? '#1a56db' : '#374151', transition: 'all .12s' }}>
                      <Icon size={12} />{label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Description */}
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Description (optionnel)</div>
          <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} placeholder="Vue panoramique sur la mer, décoration contemporaine…" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer', background: 'white', color: '#374151' }}>Annuler</button>
          <button onClick={handleSave} disabled={saving || (!form.numero && !form.nom)} style={{ padding: '9px 22px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#1a56db', color: 'white', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Enregistrement…' : <><Check size={13} style={{ marginRight: 6 }} />Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function UnitesTab() {
  const [unites, setUnites]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null); // null | 'add' | unite object
  const [filter, setFilter]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/unites`, { headers: { Authorization: `Bearer ${token()}` } });
      setUnites(await r.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const isEdit = modal && modal.id;
    const url    = isEdit ? `${API}/unites/${modal.id}` : `${API}/unites`;
    const method = isEdit ? 'PUT' : 'POST';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(form) });
    setModal(null);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette unité ?')) return;
    await fetch(`${API}/unites/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    load();
  };

  const kpis = {
    total:       unites.length,
    disponible:  unites.filter(u => u.statut === 'disponible').length,
    occupee:     unites.filter(u => u.statut === 'occupee').length,
    maintenance: unites.filter(u => u.statut === 'maintenance' || u.statut === 'hors_service').length,
  };

  const filtered = unites.filter(u => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (u.numero || '').toLowerCase().includes(q) || (u.nom || '').toLowerCase().includes(q) || (u.type_unite || '').toLowerCase().includes(q);
  });

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', fontSize: 13 }}>Chargement…</div>;

  return (
    <div>
      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total unités',    val: kpis.total,       color: '#1a56db', bg: '#eff6ff' },
          { label: 'Disponibles',     val: kpis.disponible,  color: '#16a34a', bg: '#f0fdf4' },
          { label: 'Occupées',        val: kpis.occupee,     color: '#1a56db', bg: '#eff6ff' },
          { label: 'Maint. / Indispo',val: kpis.maintenance, color: '#d97706', bg: '#fffbeb' },
        ].map(k => (
          <div key={k.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 20px' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.val}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <input
          placeholder="Rechercher par numéro, nom, type…"
          value={filter} onChange={e => setFilter(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', width: 260 }}
        />
        <button onClick={() => setModal('add')} style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#1a56db', color: 'white', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          <Plus size={15} />Ajouter une unité
        </button>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '60px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          {unites.length === 0 ? 'Aucune unité configurée — commencez par ajouter vos chambres / espaces' : 'Aucun résultat pour cette recherche'}
        </div>
      ) : (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['N°', 'Nom / Type', 'Étage', 'Capacité', 'Surface', 'Tarif / nuit', 'Caractéristiques', 'Statut', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => {
                const typeLabel = TYPES_UNITE.find(t => t.value === u.type_unite)?.label || u.type_unite;
                return (
                  <tr key={u.id} style={{ borderBottom: i < filtered.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: 14, color: '#111827' }}>{u.numero || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {u.nom && <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.nom}</div>}
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{typeLabel}</div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>{u.etage !== null && u.etage !== undefined ? (u.etage === 0 ? 'RDC' : `Étage ${u.etage}`) : '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>{u.capacite} pers.</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#374151' }}>{u.surface_m2 ? `${u.surface_m2} m²` : '—'}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 600, color: '#111827' }}>{u.tarif_base ? `${parseFloat(u.tarif_base).toLocaleString('fr-FR')} €` : '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {(u.caracteristiques || []).slice(0, 4).map(c => {
                          const item = CARACT_GROUPS.flatMap(g => g.items).find(i => i.id === c);
                          return item ? <span key={c} title={item.label} style={{ background: '#f3f4f6', borderRadius: 6, padding: '2px 7px', fontSize: 10, color: '#6b7280' }}>{item.label}</span> : null;
                        })}
                        {(u.caracteristiques || []).length > 4 && <span style={{ fontSize: 10, color: '#9ca3af' }}>+{u.caracteristiques.length - 4}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}><StatutBadge statut={u.statut} /></td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => setModal(u)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}><Pencil size={13} color="#374151" /></button>
                        <button onClick={() => handleDelete(u.id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, padding: '5px 8px', cursor: 'pointer' }}><Trash2 size={13} color="#dc2626" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ModalUnite
          unite={modal === 'add' ? null : modal}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

// ── Statistiques ─────────────────────────────────────────────────────────────

const MOIS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
const fmt = (s) => { if (!s) return '—'; const [y,m] = s.split('-'); return `${MOIS_FR[parseInt(m)-1]} ${y}`; };
const fmtEur = (v) => v ? `${Math.round(v).toLocaleString('fr-FR')} €` : '—';

const COLORS_TAUX = ['#1a56db', '#bfdbfe'];
const COLORS_BAR  = '#1a56db';

function KpiCard({ icon: Icon, label, value, sub, color = '#1a56db', bg = '#eff6ff', border = '#bfdbfe' }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${border}`, borderRadius: 14, padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ background: bg, borderRadius: 8, padding: 7, display: 'flex' }}>
          <Icon size={16} color={color} />
        </div>
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9ca3af' }}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 14 }}>{children}</div>;
}

const card = { background: 'white', border: '1px solid #e5e7eb', borderRadius: 14, padding: '20px 24px', marginBottom: 16 };

export default function Etablissement() {
  const [tab, setTab]       = useState('stats');
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/etablissement/stats`, { headers: { Authorization: `Bearer ${token()}` } });
      const d = await r.json();
      setStats(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { if (tab === 'stats') load(); }, [load, tab]);

  const TABS = [
    { id: 'stats',  label: 'Statistiques' },
    { id: 'unites', label: 'Unités / Chambres' },
  ];

  const nbChambres = stats?.nb_chambres || 0;
  const noCapacite = nbChambres === 0;
  const noRevenu   = stats?.revenus_mois === 0 && stats?.par_mois?.every(m => m.revenus === 0);

  const tauxLabel = (stats != null && stats.taux_occupation != null) ? `${stats.taux_occupation}%` : '—';
  const tauxPie = (stats != null && stats.taux_occupation != null)
    ? [{ name: 'Occupé', value: stats.taux_occupation }, { name: 'Libre', value: 100 - stats.taux_occupation }]
    : [];

  // Distribution durées — regroupe 7+ nuits
  const distData = (() => {
    if (!stats?.distribution_duree?.length) return [];
    const buckets = {};
    stats.distribution_duree.forEach(({ duree, count }) => {
      const key = duree >= 7 ? '7+' : String(duree);
      buckets[key] = (buckets[key] || 0) + count;
    });
    return Object.entries(buckets).map(([duree, count]) => ({
      duree: duree === '1' ? '1 nuit' : `${duree} nuits`,
      count,
    }));
  })();

  const parMoisFmt = (stats?.par_mois || []).map(m => ({
    ...m,
    label: fmt(m.mois),
  }));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Établissement</h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>Gestion opérationnelle</p>
        </div>
        {tab === 'stats' && (
          <button onClick={load} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 14px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#374151' }}>
            <RefreshCw size={13} /> Actualiser
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e5e7eb' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '9px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 600 : 400, color: tab === t.id ? '#1a56db' : '#6b7280', borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent', marginBottom: -1 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'unites' && <UnitesTab />}

      {tab === 'stats' && loading && <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af', fontSize: 13 }}>Chargement…</div>}

      {tab === 'stats' && !loading && stats && <>
      {noCapacite && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '12px 18px', marginBottom: 20, fontSize: 13, color: '#92400e' }}>
          Aucune unité configurée dans l'onglet "Unités / Chambres" — le taux d'occupation ne peut pas être calculé.
        </div>
      )}
      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
        <KpiCard
          icon={Building2} label="Taux d'occupation" value={tauxLabel}
          sub={noCapacite ? 'Aucune chambre dans Accès & Zones' : `${stats.nuits_occupees} / ${stats.nuits_disponibles} nuits-chambres • ${nbChambres} chambre${nbChambres > 1 ? 's' : ''}`}
          color="#1a56db" bg="#eff6ff" border="#bfdbfe"
        />
        <KpiCard
          icon={CalendarDays} label="Réservations ce mois" value={stats?.reservations_mois ?? '—'}
          sub="Arrivées enregistrées"
          color="#7c3aed" bg="#f5f3ff" border="#ddd6fe"
        />
        <KpiCard
          icon={Clock} label="Durée moyenne de séjour" value={stats?.duree_moyenne ? `${stats.duree_moyenne} nuits` : '—'}
          sub="Sur le mois en cours"
          color="#0d9488" bg="#f0fdfa" border="#99f6e4"
        />
        <KpiCard
          icon={TrendingUp} label="Revenus estimés" value={fmtEur(stats?.revenus_mois)}
          sub={noRevenu ? 'Ajouter prix/nuit sur les fiches' : 'Basé sur prix/nuit × durée'}
          color="#d97706" bg="#fffbeb" border="#fde68a"
        />
      </div>

      {/* Row 1 : Occupation + Réservations */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

        {/* Taux d'occupation */}
        <div style={card}>
          <SectionTitle>Taux d'occupation mensuel</SectionTitle>
          {noCapacite ? (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>Configurez le nombre de chambres</div>
          ) : tauxPie.length ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
              <PieChart width={140} height={140}>
                <Pie data={tauxPie} cx={65} cy={65} innerRadius={42} outerRadius={62} dataKey="value" startAngle={90} endAngle={-270}>
                  <Cell fill="#1a56db" />
                  <Cell fill="#e0e7ff" />
                </Pie>
              </PieChart>
              <div>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#111827' }}>{stats.taux_occupation}%</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{stats.nuits_occupees} nuits occupées</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{stats.nb_chambres} chambre{stats.nb_chambres > 1 ? 's' : ''} × {new Date().toLocaleString('fr-FR', { month: 'long' })}</div>
              </div>
            </div>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>Aucune donnée ce mois</div>
          )}

          {parMoisFmt.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginTop: 20, marginBottom: 8 }}>Tendance 12 mois</div>
              <ResponsiveContainer width="100%" height={80}>
                <AreaChart data={parMoisFmt}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => [`${v}%`, 'Taux']} />
                  <Area type="monotone" dataKey="taux" stroke="#1a56db" fill="#eff6ff" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

        {/* Réservations mensuelles */}
        <div style={card}>
          <SectionTitle>Réservations mensuelles</SectionTitle>
          {parMoisFmt.length ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={parMoisFmt} barSize={16}>
                <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, 'Réservations']} />
                <Bar dataKey="reservations" fill={COLORS_BAR} radius={[4,4,0,0]}>
                  {parMoisFmt.map((_, i) => (
                    <Cell key={i} fill={i === parMoisFmt.length - 1 ? '#1a56db' : '#bfdbfe'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '60px 0' }}>Aucune réservation enregistrée</div>
          )}
          <div style={{ display: 'flex', gap: 20, marginTop: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Ce mois</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>{stats?.reservations_mois ?? 0}</div>
            </div>
            {parMoisFmt.length > 1 && (
              <div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Mois précédent</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#6b7280' }}>
                  {parMoisFmt[parMoisFmt.length - 2]?.reservations ?? 0}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Row 2 : Durée de séjour + Revenus */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Durée de séjour */}
        <div style={card}>
          <SectionTitle>Durée de séjour</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>{stats?.duree_moyenne || '—'}</span>
            {stats?.duree_moyenne > 0 && <span style={{ fontSize: 13, color: '#6b7280' }}>nuits en moyenne</span>}
          </div>
          {distData.length ? (
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={distData} barSize={20}>
                <XAxis dataKey="duree" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip formatter={(v) => [v, 'Séjours']} />
                <Bar dataKey="count" radius={[4,4,0,0]}>
                  {distData.map((_, i) => (
                    <Cell key={i} fill={['#0d9488','#14b8a6','#5eead4','#99f6e4','#ccfbf1'][i % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>Aucune donnée récente</div>
          )}
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>Distribution sur les 3 derniers mois</div>
        </div>

        {/* Revenus estimés */}
        <div style={card}>
          <SectionTitle>Revenus estimés</SectionTitle>
          {noRevenu ? (
            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '60px 0' }}>
              Renseignez un prix/nuit sur chaque fiche client pour estimer les revenus
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 16 }}>
                <span style={{ fontSize: 32, fontWeight: 700, color: '#111827' }}>{fmtEur(stats?.revenus_mois)}</span>
                <span style={{ fontSize: 13, color: '#6b7280' }}>ce mois</span>
              </div>
              <ResponsiveContainer width="100%" height={140}>
                <AreaChart data={parMoisFmt}>
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(v) => [`${Math.round(v).toLocaleString('fr-FR')} €`, 'Revenus']} />
                  <Area type="monotone" dataKey="revenus" stroke="#d97706" fill="#fffbeb" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </>
          )}
        </div>

      </div>
      </>}
    </div>
  );
}
